(() => {
  "use strict";

  window.BYL = window.BYL || {};

  const services = BYL.services;

  if (!services) {
    throw new Error(
      "BYL order engine requires byl-services.js to load first."
    );
  }


  /* =========================================================
     CONSTANTS
     ========================================================= */

  const ORDER_MODES = {
    DATE_ITEMS: "DATE_ITEMS",
    MONTHLY_WEEKDAYS: "MONTHLY_WEEKDAYS"
  };


  /* =========================================================
     BASIC HELPERS
     ========================================================= */

  function cleanText(value) {
    return String(value || "")
      .trim()
      .replace(/\s+/g, " ");
  }


  function normalize(value) {
    return cleanText(value)
      .toLowerCase();
  }


  function normalizeStatus(value) {
    return normalize(value)
      .replace(/\s+/g, "_");
  }


  function unique(values) {
    return [
      ...new Set(
        (values || [])
          .map(cleanText)
          .filter(Boolean)
      )
    ];
  }


  function localDate(
    isoDate,
    time = "12:00"
  ) {
    if (!isoDate) {
      return null;
    }

    const date =
      new Date(
        `${isoDate}T${time}:00`
      );

    return Number.isNaN(
      date.getTime()
    )
      ? null
      : date;
  }


  function dateIso(date) {
    if (!(date instanceof Date)) {
      return "";
    }

    const year =
      date.getFullYear();

    const month =
      String(
        date.getMonth() + 1
      ).padStart(2, "0");

    const day =
      String(
        date.getDate()
      ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }


  function parseDateValue(value) {
    const text =
      cleanText(value);

    if (!text) {
      return "";
    }

    if (
      /^\d{4}-\d{2}-\d{2}$/.test(text)
    ) {
      return text;
    }

    const monthDay =
      text.match(
        /\b(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)?\.?,?\s*(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\.?[\s,]+(\d{1,2})(?:,\s*(\d{4}))?\b/i
      );

    if (monthDay) {
      const monthMap = {
        jan: "01",
        feb: "02",
        mar: "03",
        apr: "04",
        may: "05",
        jun: "06",
        jul: "07",
        aug: "08",
        sep: "09",
        sept: "09",
        oct: "10",
        nov: "11",
        dec: "12"
      };

      const month =
        monthMap[
          monthDay[1]
            .toLowerCase()
            .replace(".", "")
        ];

      if (month) {
        return `${monthDay[3] || new Date().getFullYear()}-${month}-${String(
          monthDay[2]
        ).padStart(2, "0")}`;
      }
    }

    const parsed =
      new Date(text);

    if (
      Number.isNaN(
        parsed.getTime()
      )
    ) {
      return "";
    }

    return dateIso(parsed);
  }


  function formatDate(
    isoDate
  ) {
    const date =
      localDate(
        isoDate,
        "12:00"
      );

    if (!date) {
      return isoDate || "";
    }

    return new Intl.DateTimeFormat(
      "en-CA",
      {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric"
      }
    ).format(date);
  }


  function formatDeadline(
    date
  ) {
    if (!(date instanceof Date)) {
      return "";
    }

    return new Intl.DateTimeFormat(
      "en-CA",
      {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit"
      }
    ).format(date);
  }


  /* =========================================================
     BRAND HELPERS
     ========================================================= */

  function getOrderMode(
    brand
  ) {
    const mode =
      cleanText(
        brand?.orderMode
      ).toUpperCase();

    return (
      mode ===
      ORDER_MODES.MONTHLY_WEEKDAYS
    )
      ? ORDER_MODES.MONTHLY_WEEKDAYS
      : ORDER_MODES.DATE_ITEMS;
  }


  function itemsForDate(brand, date = "") {
    const items = Array.isArray(brand?.items) ? brand.items : [];
    if (!date || !brand?.dateMenus) return items;
    const periods = brand.dateMenus[date] || ["current"];
    return items.filter(item =>
      !Array.isArray(item.menuMembership) ||
      item.menuMembership.some(period => periods.includes(period))
    );
  }


  function mainItems(
    brand,
    profile = null,
    date = ""
  ) {
    const items =
      itemsForDate(brand, date);

    const mains =
      items.filter(
        item =>
          itemAvailableForProfile(
            item,
            profile
          ) &&
          normalize(
            item.itemType
          ) === "main"
      );

    if (mains.length) {
      return mains;
    }

    return items.filter(
      item => {
        const type =
          normalize(
            item.itemType
          );

        return (
          itemAvailableForProfile(
            item,
            profile
          ) &&
          type !== "add-on" &&
          type !== "addon"
        );
      }
    );
  }


  function addOnItems(
    brand,
    profile = null,
    date = ""
  ) {
    return (
      itemsForDate(brand, date)
    ).filter(item => {
      const type =
        normalize(
          item.itemType
        );

      return (
        itemAvailableForProfile(
          item,
          profile
        ) &&
        (
          type === "add-on" ||
          type === "addon"
        )
      );
    });
  }


  function audienceList(
    record
  ) {
    const raw =
      record?.audience ??
      record?.audiences ??
      record?.memberTypes ??
      record?.memberType;

    if (Array.isArray(raw)) {
      return raw
        .map(normalize)
        .filter(Boolean);
    }

    return cleanText(raw)
      .split(/[,|]/)
      .map(normalize)
      .filter(Boolean);
  }


  function isStaffAudienceBrand(
    record
  ) {
    const brandId = normalize(
      record?.brandId
    );

    return (
      brandId.endsWith("-staff") ||
      normalize(
        record?.brandName
      ) ===
      "booster juice staff"
    );
  }


  function isStaffSmoothieItem(
    record
  ) {
    const name =
      normalize(
        record?.itemName
      );

    return (
      name.includes(
        "staff"
      ) &&
      name.includes(
        "smoothie"
      )
    );
  }


  function staffSmoothieSize(
    record
  ) {
    const match =
      cleanText(
        record?.itemName
      ).match(
        /\b(\d+\s*oz)\b/i
      );

    return match
      ? match[1].replace(/\s+/g, "")
      : "";
  }


  function staffSmoothieOrderValue(
    item,
    selection
  ) {
    const smoothie =
      cleanText(
        selection?.customInputs
          ?.Smoothie
      );

    if (
      !smoothie ||
      !isStaffSmoothieItem(
        item
      )
    ) {
      return "";
    }

    return cleanText(
      [
        staffSmoothieSize(
          item
        ),
        smoothie
      ].filter(Boolean)
        .join(" ")
    );
  }


  function recordAvailableForProfile(
    record,
    profile = null
  ) {
    const audience =
      audienceList(record);

    if (!audience.length) {
      if (
        isStaffAudienceBrand(
          record
        ) ||
        isStaffSmoothieItem(
          record
        )
      ) {
        return isStaffAudienceProfile(
          profile
        );
      }

      return true;
    }

    const memberType = profileAudienceType(
      profile
    );

    return audience.includes(
      memberType
    );
  }


  function profileMemberType(
    profile
  ) {
    const direct =
      normalize(
        profile?.memberType
      );

    if (direct) {
      return direct;
    }

    const classroom =
      normalize(
        profile?.classroom ||
        profile?.teacher ||
        profile?.Teacher
      );

    return [
      "staff",
      "office"
    ].includes(classroom)
      ? classroom
      : "student";
  }


  function isStaffAudienceProfile(
    profile
  ) {
    return [
      "staff",
      "office"
    ].includes(
      profileMemberType(profile)
    );
  }


  function profileAudienceType(
    profile
  ) {
    return isStaffAudienceProfile(profile)
      ? "staff"
      : profileMemberType(profile);
  }


  function itemAvailableForProfile(
    item,
    profile = null
  ) {
    return recordAvailableForProfile(
      item,
      profile
    );
  }


  function brandAvailableForProfile(
    brand,
    profile = null
  ) {
    const hasItems =
      mainItems(
        brand,
        profile
      ).length > 0;

    const hasPeriods =
      (brand?.periods || [])
        .some(
          period =>
            Array.isArray(
              period?.options
            ) &&
            period.options.length > 0
        );

    return (
      recordAvailableForProfile(
        brand,
        profile
      ) &&
      (
        hasItems ||
        hasPeriods
      )
    );
  }


  function toppingOptions(
    brand
  ) {
    return (
      Array.isArray(brand?.options)
        ? brand.options
        : []
    )
      .filter(
        option =>
          normalize(
            option.type
          ) === "topping"
      )
      .sort(
        (a, b) =>
          Number(
            a.sortOrder || 0
          ) -
          Number(
            b.sortOrder || 0
          )
      );
  }


  function choiceOptions(
    brand
  ) {
    return (
      Array.isArray(brand?.options)
        ? brand.options
        : []
    )
      .filter(
        option =>
          normalize(
            option.type
          ) === "choice"
      )
      .sort(
        (a, b) =>
          Number(
            a.sortOrder || 0
          ) -
          Number(
            b.sortOrder || 0
          )
      );
  }


  function genericOptions(
    brand
  ) {
    return (
      Array.isArray(brand?.options)
        ? brand.options
        : []
    )
      .filter(option => {
        const type =
          normalize(
            option.type
          );

        return (
          type !== "choice" &&
          type !== "topping" &&
          type !== "add-on" &&
          type !== "addon"
        );
      })
      .sort(
        (a, b) =>
          Number(
            a.sortOrder || 0
          ) -
          Number(
            b.sortOrder || 0
          )
      );
  }


  /* =========================================================
     CUTOFF ENGINE
     ========================================================= */

  const WEEKDAYS = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6
  };


  function cutoffForServiceDate(
    serviceDate,
    brand
  ) {
    const cutoff =
      brand?.cutoff || {};

    const cutoffTime =
      cleanText(
        cutoff.time
      ) || "20:00";

    const base =
      localDate(
        serviceDate,
        cutoffTime
      );

    if (!base) {
      return null;
    }

    const cutoffWeekday =
      normalize(
        cutoff.weekday
      );

    if (
      cutoffWeekday &&
      WEEKDAYS[
        cutoffWeekday
      ] !== undefined
    ) {
      const desired =
        WEEKDAYS[
          cutoffWeekday
        ];

      const result =
        new Date(
          base.getTime()
        );

      do {
        result.setDate(
          result.getDate() - 1
        );
      } while (
        result.getDay() !==
        desired
      );

      return result;
    }

    let daysBefore =
      Number(
        cutoff.daysBefore || 0
      );

    if (
      base.getDay() === 1
    ) {
      daysBefore +=
        Number(
          cutoff.mondayExtraDays || 0
        );
    }

    const result =
      new Date(
        base.getTime()
      );

    result.setDate(
      result.getDate() -
      daysBefore
    );

    return result;
  }


  function isServiceDateOpen(
    serviceDate,
    brand,
    now = new Date()
  ) {
    const deadline =
      cutoffForServiceDate(
        serviceDate,
        brand
      );

    if (!deadline) {
      return false;
    }

    return (
      now.getTime() <=
      deadline.getTime()
    );
  }


  function getOpenDates(
    brand,
    now = new Date()
  ) {
    return (
      Array.isArray(brand?.dates)
        ? brand.dates
        : []
    )
      .filter(
        date =>
          isServiceDateOpen(
            date,
            brand,
            now
          )
      )
      .sort();
  }


  function getCutoffLabel(
    serviceDate,
    brand
  ) {
    return formatDeadline(
      cutoffForServiceDate(
        serviceDate,
        brand
      )
    );
  }


  function periodDeadline(
    period
  ) {
    if (!period?.cutoffDate) {
      return null;
    }

    return localDate(
      period.cutoffDate,
      cleanText(
        period.cutoffTime
      ) || "20:00"
    );
  }


  function isPeriodOpen(
    period,
    now = new Date()
  ) {
    const deadline =
      periodDeadline(
        period
      );

    if (!deadline) {
      return true;
    }

    return (
      now.getTime() <=
      deadline.getTime()
    );
  }


  function getOpenPeriods(
    brand,
    now = new Date()
  ) {
    return (
      Array.isArray(brand?.periods)
        ? brand.periods
        : []
    ).filter(
      period =>
        isPeriodOpen(
          period,
          now
        )
    );
  }


  /* =========================================================
     ORDER HISTORY
     ========================================================= */

  function isOrderLineActive(
    line
  ) {
    if (!line) {
      return false;
    }

    if (
      line.isRefunded === true
    ) {
      return false;
    }

    if (
      line.isCancelled === true ||
      line.cancelledAt
    ) {
      return false;
    }

    const financial =
      normalizeStatus(
        line.financialStatus
      );

    if (
      financial ===
        "refunded" ||
      financial ===
        "voided"
    ) {
      return false;
    }

    return true;
  }


  function activeOrderLines(
    orders
  ) {
    return (
      Array.isArray(orders)
        ? orders
        : []
    ).filter(
      isOrderLineActive
    );
  }


  function lineProperties(
    line
  ) {
    return (
      line?.properties &&
      typeof line.properties ===
        "object"
    )
      ? line.properties
      : {};
  }


function lineServiceDate(
  line
) {
  const props =
    lineProperties(line);

  const serviceDate =
    cleanText(
      props._BYLServiceDate
    ) ||
    cleanText(
      props._BYLDateISO
    ) ||
    parseDateValue(
      props.Date
    );

  if (serviceDate) {
    return serviceDate;
  }

  // Monthly programs such as Daily Milk
  if (
    props._BYLPeriodId &&
    props.Day
  ) {
    return `${cleanText(
      props._BYLPeriodId
    )}:${cleanText(
      props.Day
    )}`;
  }

  // Legacy monthly fallback
  if (
    props.Period &&
    props.Day
  ) {
    return `${cleanText(
      props.Period
    )}:${cleanText(
      props.Day
    )}`;
  }

  return "";
}


  function lineMatchesSchool(
    line,
    school
  ) {
    const props =
      lineProperties(line);

    const schoolId =
      cleanText(
        props._schoolId ||
        props._BYLSchoolId
      );

    if (
      schoolId &&
      schoolId ===
        cleanText(
          school?.schoolId
        )
    ) {
      return true;
    }

    return (
      normalize(
        props.School
      ) &&
      normalize(
        props.School
      ) ===
        normalize(
          school?.schoolName
        )
    );
  }


  function lineMatchesProfile(
    line,
    profile
  ) {
    const props =
      lineProperties(line);

    if (
      props._BYLProfileId &&
      cleanText(
        props._BYLProfileId
      ) ===
        cleanText(
          profile?.id
        )
    ) {
      return true;
    }

    const lineName =
      cleanText(
        props.Name ||
        props.Student ||
        props["Student Name"]
      );

    if (!lineName) {
      return false;
    }

    const fullName =
      cleanText(
        `${profile?.firstName || ""} ${
          profile?.lastName || ""
        }`
      );

    if (
      normalize(lineName) ===
      normalize(fullName)
    ) {
      return true;
    }

    const lastInitial =
      cleanText(
        profile?.lastName
      ).charAt(0);

    const legacyName =
      cleanText(
        `${profile?.firstName || ""} ${
          lastInitial
        }`
      );

    if (
      legacyName &&
      normalize(lineName) ===
      normalize(legacyName)
    ) {
      return true;
    }

    return (
      normalize(lineName) ===
      normalize(
        profile?.firstName
      )
    );
  }


  function lineBrandId(
    line,
    school
  ) {
    const props =
      lineProperties(line);

    if (
      props._BYLBrandId
    ) {
      return cleanText(
        props._BYLBrandId
      );
    }

    const variantId =
      cleanText(
        line?.variantId
      );

    if (!variantId) {
      return "";
    }

    for (
      const brand
      of school?.brands || []
    ) {
      const hasItem =
        (
          brand.items || []
        ).some(
          item =>
            cleanText(
              item.variantId
            ) === variantId
        );

      if (hasItem) {
        return brand.brandId;
      }

      const hasPeriodVariant =
        (
          brand.periods || []
        ).some(period =>
          (
            period.options || []
          ).some(
            option =>
              cleanText(
                option.variantId
              ) === variantId
          )
        );

      if (hasPeriodVariant) {
        return brand.brandId;
      }
    }

    return "";
  }


  function findExistingOrder({
    orders,
    profile,
    school,
    brand,
    serviceDate
  }) {
    if (
      !profile ||
      !school ||
      !brand ||
      !serviceDate
    ) {
      return null;
    }

    return (
      activeOrderLines(
        orders
      ).find(line => {
        if (
          !lineMatchesProfile(
            line,
            profile
          )
        ) {
          return false;
        }

        if (
          !lineMatchesSchool(
            line,
            school
          )
        ) {
          return false;
        }

        if (
          cleanText(
            lineBrandId(
              line,
              school
            )
          ) !==
          cleanText(
            brand.brandId
          )
        ) {
          return false;
        }

        return (
          lineServiceDate(
            line
          ) === serviceDate
        );
      }) || null
    );
  }


  /* =========================================================
     SELECTION STATE
     ========================================================= */

  function createSelections() {
    return {
      dates: {},
      toppings: [],
      options: {},
      periods: {}
    };
  }


  function ensureSelections(
    selections
  ) {
    return {
      dates:
        selections?.dates &&
        typeof selections.dates ===
          "object"
          ? selections.dates
          : {},

      toppings:
        Array.isArray(
          selections?.toppings
        )
          ? selections.toppings
          : [],

      options:
        selections?.options &&
        typeof selections.options ===
          "object"
          ? selections.options
          : {},

      periods:
        selections?.periods &&
        typeof selections.periods ===
          "object"
          ? selections.periods
          : {}
    };
  }


  function setMainItem(
    selections,
    date,
    item
  ) {
    const next =
      ensureSelections(
        selections
      );

    if (!item?.variantId) {
      delete next.dates[date];
      return next;
    }

    const existing =
      next.dates[date] || {};

    next.dates[date] = {
      date,

      variantId:
        cleanText(
          item.variantId
        ),

      itemName:
        cleanText(
          item.itemName
        ),

      itemPrice:
        priceFromRecord(
          item
        ),

      customInputs: {},

      addOns:
        Array.isArray(
          existing.addOns
        )
          ? existing.addOns
          : []
    };

    return next;
  }


  function toggleAddOn(
    selections,
    date,
    item
  ) {
    const next =
      ensureSelections(
        selections
      );

    const main =
      next.dates[date];

    if (
      !main ||
      !item?.variantId
    ) {
      return next;
    }

    main.addOns =
      Array.isArray(
        main.addOns
      )
        ? main.addOns
        : [];

    const variantId =
      cleanText(
        item.variantId
      );

    const index =
      main.addOns.findIndex(
        addon =>
          cleanText(
            addon.variantId
          ) === variantId
      );

    if (index >= 0) {
      main.addOns.splice(
        index,
        1
      );
    } else {
      main.addOns.push({
        variantId,

        itemName:
          cleanText(
            item.itemName
          ),

        itemPrice:
          priceFromRecord(
            item
          )
      });
    }

    return next;
  }


  function toggleTopping(
    selections,
    topping
  ) {
    const next =
      ensureSelections(
        selections
      );

    const value =
      cleanText(topping);

    if (!value) {
      return next;
    }

    const exists =
      next.toppings.some(
        item =>
          normalize(item) ===
          normalize(value)
      );

    next.toppings =
      exists
        ? next.toppings.filter(
            item =>
              normalize(item) !==
              normalize(value)
          )
        : [
            ...next.toppings,
            value
          ];

    return next;
  }


  function setGenericOption(
    selections,
    group,
    value
  ) {
    const next =
      ensureSelections(
        selections
      );

    const cleanGroup =
      cleanText(group);

    if (!cleanGroup) {
      return next;
    }

    if (!value) {
      delete next.options[
        cleanGroup
      ];

      return next;
    }

    next.options[
      cleanGroup
    ] =
      cleanText(value);

    return next;
  }


  function setPeriodChoice(
    selections,
    periodId,
    label,
    choice
  ) {
    const next =
      ensureSelections(
        selections
      );

    const cleanPeriod =
      cleanText(
        periodId
      );

    const cleanLabel =
      cleanText(
        label
      );

    if (
      !cleanPeriod ||
      !cleanLabel
    ) {
      return next;
    }

    if (
      !next.periods[
        cleanPeriod
      ]
    ) {
      next.periods[
        cleanPeriod
      ] = {};
    }

    next.periods[
      cleanPeriod
    ][
      cleanLabel
    ] = {
      choice:
        cleanText(
          choice?.value
        ),

      submit:
        choice?.submit !== false,

      variantId:
        cleanText(
          choice?.variantId
        ),

      label:
        cleanLabel,

      itemPrice:
        priceFromRecord(
          choice
        )
    };

    return next;
  }


  function customInputConfig(
    item
  ) {
    const config =
      item?.customInput ||
      item?.custom_input ||
      null;

    if (
      config &&
      typeof config === "object"
    ) {
      return config;
    }

    if (
      cleanText(config)
    ) {
      return {
        property:
          cleanText(config)
      };
    }

    if (
      isStaffSmoothieItem(
        item
      )
    ) {
      return {
        property:
          "Smoothie",

        label:
          "Smoothie",

        placeholder:
          "Enter smoothie name",

        required:
          true
      };
    }

    return null;
  }


  function customInputProperty(
    config
  ) {
    return cleanText(
      config?.property ||
      config?.name ||
      config?.label ||
      "Order"
    );
  }


  function setCustomInput(
    selections,
    date,
    property,
    value
  ) {
    const next =
      ensureSelections(
        selections
      );

    const selection =
      next.dates?.[date];

    if (!selection) {
      return next;
    }

    const key =
      cleanText(property);

    if (!key) {
      return next;
    }

    selection.customInputs =
      selection.customInputs &&
      typeof selection.customInputs ===
        "object"
        ? selection.customInputs
        : {};

    const cleanValue =
      cleanText(value);

    if (cleanValue) {
      selection.customInputs[key] =
        cleanValue;
    } else {
      delete selection.customInputs[key];
    }

    return next;
  }


  function selectedMainItem(
    brand,
    selection,
    profile = null,
    date = ""
  ) {
    if (!selection?.variantId) {
      return null;
    }

    return mainItems(
      brand,
      profile,
      date
    ).find(
      item =>
        cleanText(
          item.variantId
        ) ===
        cleanText(
          selection.variantId
        )
    ) || null;
  }


  function missingRequiredCustomInputs({
    brand,
    selections,
    profile = null
  }) {
    const safe =
      ensureSelections(
        selections
      );

    const missing = [];

    for (
      const [
        date,
        selection
      ]
      of Object.entries(
        safe.dates
      )
    ) {
      const item =
        selectedMainItem(
          brand,
          selection,
          profile,
          date
        );

      const config =
        customInputConfig(
          item
        );

      if (
        !config ||
        config.required === false
      ) {
        continue;
      }

      const property =
        customInputProperty(
          config
        );

      if (
        !cleanText(
          selection.customInputs
            ?.[property]
        )
      ) {
        missing.push({
          date,
          property,
          label:
            cleanText(
              config.label
            ) ||
            property,
          itemName:
            cleanText(
              item?.itemName
            )
        });
      }
    }

    return missing;
  }


  /* =========================================================
     PRICE / TOTALS
     ========================================================= */

  function normalizePriceCents(
    value
  ) {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return null;
    }

    if (
      typeof value === "string" &&
      value.trim().includes(".")
    ) {
      const dollars =
        Number(value);

      return Number.isFinite(dollars)
        ? Math.round(dollars * 100)
        : null;
    }

    const cents =
      Number(value);

    return Number.isFinite(cents)
      ? cents
      : null;
  }


  function priceFromRecord(
    record
  ) {
    if (!record) {
      return null;
    }

    const direct =
      record.priceCents ??
      record.price_cents ??
      record.variantPriceCents ??
      record.variant_price_cents;

    if (
      direct !== null &&
      direct !== undefined &&
      direct !== ""
    ) {
      return normalizePriceCents(
        direct
      );
    }

    const amount =
      record.price ??
      record.amount ??
      record.variantPrice ??
      record.variant_price;

    if (
      amount === null ||
      amount === undefined ||
      amount === ""
    ) {
      return null;
    }

    const numeric =
      Number(amount);

    if (!Number.isFinite(numeric)) {
      return null;
    }

    return numeric > 100
      ? Math.round(numeric)
      : Math.round(numeric * 100);
  }


  function fallbackPriceForVariant(
    brand,
    variantId
  ) {
    const id =
      cleanText(variantId);

    if (!id || !brand) {
      return null;
    }

    for (
      const item
      of brand.items || []
    ) {
      if (
        cleanText(
          item.variantId
        ) === id
      ) {
        return priceFromRecord(
          item
        );
      }
    }

    for (
      const period
      of brand.periods || []
    ) {
      for (
        const option
        of period.options || []
      ) {
        if (
          cleanText(
            option.variantId
          ) === id
        ) {
          return priceFromRecord(
            option
          );
        }
      }
    }

    return null;
  }


  function getSelectionLines({
    brand,
    selections,
    periodId
  }) {
    const safe =
      ensureSelections(
        selections
      );

    const mode =
      getOrderMode(
        brand
      );

    const result = [];

    if (
      mode ===
      ORDER_MODES.MONTHLY_WEEKDAYS
    ) {
      const periodSelections =
        safe.periods[
          cleanText(periodId)
        ] || {};

      for (
        const selection
        of Object.values(
          periodSelections
        )
      ) {
        if (
          selection.submit ===
            false ||
          !selection.variantId
        ) {
          continue;
        }

        result.push({
          type:
            "period",

          variantId:
            selection.variantId,

          itemPrice:
            selection.itemPrice,

          label:
            selection.label,

          choice:
            selection.choice
        });
      }

      return result;
    }

    for (
      const [
        date,
        selection
      ]
      of Object.entries(
        safe.dates
      )
    ) {
      if (
        !selection?.variantId
      ) {
        continue;
      }

      result.push({
        type:
          "main",

        date,

        variantId:
          selection.variantId,

        itemPrice:
          selection.itemPrice,

        itemName:
          selection.itemName
      });

      for (
        const addon
        of selection.addOns || []
      ) {
        if (
          !addon?.variantId
        ) {
          continue;
        }

        result.push({
          type:
            "addon",

          date,

          variantId:
            addon.variantId,

          itemPrice:
            addon.itemPrice,

          itemName:
            addon.itemName,

          parentItemName:
            selection.itemName
        });
      }
    }

    return result;
  }


  function calculateTotal({
    brand,
    selections,
    periodId
  }) {
    const lines =
      getSelectionLines({
        brand,
        selections,
        periodId
      });

    let total = 0;
    let unknownPrices = false;

    for (const line of lines) {
      let price =
        services.variants.getPrice(
          line.variantId
        );

      if (
        price === null ||
        price === undefined
      ) {
        price =
          normalizePriceCents(
            line.itemPrice
          ) ??
          fallbackPriceForVariant(
            brand,
            line.variantId
          );
      }

      if (
        price === null ||
        price === undefined
      ) {
        unknownPrices = true;
      } else {
        total +=
          Number(price);
      }
    }

    return {
      lineCount:
        lines.length,

      total,

      unknownPrices,

      formatted:
        unknownPrices
          ? (
              total > 0
                ? `${services.money.format(total)}+`
                : "Loading price..."
            )
          : services.money.format(
              total
            )
    };
  }


  /* =========================================================
     CART PROPERTIES
     ========================================================= */

  function profileName(
    profile
  ) {
    return cleanText(
      `${
        profile?.firstName || ""
      } ${
        profile?.lastName || ""
      }`
    );
  }


  function allergyText(
    profile
  ) {
    if (
      BYL.customer
        ?.allergies
        ?.toText
    ) {
      return (
        BYL.customer
          .allergies
          .toText(profile)
      );
    }

    return cleanText(
      profile?.notes
    );
  }


  function publicBaseProperties({
    school,
    profile
  }) {
    const properties = {};

    if (
      cleanText(
        school?.city
      )
    ) {
      properties.City =
        cleanText(
          school.city
        );
    }

    properties.School =
      cleanText(
        school?.schoolName
      );

    properties.Name =
      profileName(
        profile
      );

    const memberType =
      profileMemberType(profile);

    properties.Teacher = [
      "staff",
      "office"
    ].includes(memberType)
      ? memberType.toUpperCase()
      : cleanText(
          profile?.classroom
        );

    const allergies =
      allergyText(
        profile
      );

    if (allergies) {
      properties.Allergies =
        allergies;
    }

    return properties;
  }


  function hiddenBaseProperties({
    school,
    profile,
    brand
  }) {
    return {
      _schoolId:
        cleanText(
          school?.schoolId
        ),

      _BYLSchoolId:
        cleanText(
          school?.schoolId
        ),

      _BYLBrandId:
        cleanText(
          brand?.brandId
        ),

      _BYLProfileId:
        cleanText(
          profile?.id
        )
    };
  }

  function selectionAvailableForDate(brand, selection, profile, date) {
    if (!selectedMainItem(brand, selection, profile, date)) return false;
    const addons = addOnItems(brand, profile, date);
    return (selection.addOns || []).every(addon =>
      addons.some(item => cleanText(item.variantId) === cleanText(addon.variantId))
    );
  }


  function invalidDateSelections({ brand, selections, profile = null }) {
    if (getOrderMode(brand) !== ORDER_MODES.DATE_ITEMS) return [];
    return Object.entries(ensureSelections(selections).dates)
      .filter(([date, selection]) => selection?.variantId &&
        !selectionAvailableForDate(brand, selection, profile, date))
      .map(([date]) => date);
  }


  function cutoffSnapshotProperties(serviceDate, brand) {
    const cutoff = cutoffForServiceDate(serviceDate, brand);

    return cutoff
      ? { _BYLCutoffAt: cutoff.toISOString() }
      : {};
  }


  function periodCutoffSnapshotProperties(period) {
    const cutoff = periodDeadline(period);

    return cutoff
      ? { _BYLCutoffAt: cutoff.toISOString() }
      : {};
  }


  function toppingProperty(
    selections,
    brand,
    mainItem
  ) {
    const selected =
      unique(
        ensureSelections(
          selections
        ).toppings
      );

    const itemName =
      normalize(
        mainItem?.itemName
      );

    const itemNames =
      mainItems(brand)
        .map(item =>
          normalize(
            item?.itemName
          )
        )
        .filter(Boolean);

    const options =
      toppingOptions(brand);

    const groupTerms = group =>
      cleanText(group)
        .split(/\s*(?:\/|,|&|\bor\b)\s*/i)
        .map(normalize)
        .filter(Boolean);

    const groupTargetsMenuItems = group => {
      const terms =
        groupTerms(group);

      return terms.some(term =>
        itemNames.some(name =>
          name.includes(term)
        )
      );
    };

    const appliesToItem = option => {
      const terms =
        groupTerms(
          option?.group
        );

      if (
        !terms.length ||
        !groupTargetsMenuItems(
          option?.group
        )
      ) {
        return true;
      }

      return terms.some(term =>
        itemName.includes(term)
      );
    };

    const toppings =
      selected.filter(value => {
        const matches =
          options.filter(option =>
            normalize(
              option?.value
            ) === normalize(value)
          );

        return (
          !matches.length ||
          matches.some(
            appliesToItem
          )
        );
      });

    return toppings.join(", ");
  }


  function genericOptionProperties(
    selections
  ) {
    const safe =
      ensureSelections(
        selections
      );

    const result = {};

    for (
      const [
        group,
        value
      ]
      of Object.entries(
        safe.options
      )
    ) {
      if (
        cleanText(group) &&
        cleanText(value)
      ) {
        result[
          cleanText(group)
        ] =
          cleanText(value);
      }
    }

    return result;
  }


  /* =========================================================
     CART BUILDING
     ========================================================= */

  function buildDateCartItems({
    school,
    profile,
    brand,
    selections
  }) {
    const safe =
      ensureSelections(
        selections
      );

    const publicBase =
      publicBaseProperties({
        school,
        profile
      });

    const hiddenBase =
      hiddenBaseProperties({
        school,
        profile,
        brand
      });

    const genericProps =
      genericOptionProperties(
        safe
      );

    const items = [];

    for (
      const [
        date,
        selection
      ]
      of Object.entries(
        safe.dates
      )
    ) {
      if (
        !selection?.variantId
      ) {
        continue;
      }

      if (!selectionAvailableForDate(brand, selection, profile, date)) {
        continue;
      }

      const mainProperties = {
        ...publicBase,

        Date:
          formatDate(date)
      };

      const mainItem =
        selectedMainItem(
          brand,
          selection,
          profile,
          date
        );

      const toppings =
        toppingProperty(
          safe,
          brand,
          mainItem || selection
        );

      const smoothieOrder =
        staffSmoothieOrderValue(
          mainItem,
          selection
        );

      if (
        smoothieOrder
      ) {
        mainProperties.Smoothie =
          smoothieOrder;
      } else if (
        selection.customInputs &&
        typeof selection.customInputs ===
          "object"
      ) {
        Object.assign(
          mainProperties,
          selection.customInputs
        );
      }

      if (toppings) {
        mainProperties.Toppings =
          toppings;
      }

      Object.assign(
        mainProperties,
        genericProps
      );

      Object.assign(
        mainProperties,
        hiddenBase,
        {
          _BYLServiceDate:
            date,

          ...cutoffSnapshotProperties(
            date,
            brand
          ),

          _BYLItemType:
            "Main"
        }
      );

      items.push({
        id:
          Number(
            selection.variantId
          ),

        quantity: 1,

        properties:
          mainProperties
      });

      for (
        const addon
        of selection.addOns || []
      ) {
        if (
          !addon?.variantId
        ) {
          continue;
        }

        const addonProperties = {
          ...publicBase,

          Date:
            formatDate(date),

          AddOnFor:
            cleanText(
              selection.itemName
            ),

          ...hiddenBase,

          _BYLServiceDate:
            date,

          ...cutoffSnapshotProperties(
            date,
            brand
          ),

          _BYLItemType:
            "Add-on"
        };

        items.push({
          id:
            Number(
              addon.variantId
            ),

          quantity: 1,

          properties:
            addonProperties
        });
      }
    }

    return items;
  }


  function buildMonthlyCartItems({
    school,
    profile,
    brand,
    selections,
    periodId
  }) {
    const safe =
      ensureSelections(
        selections
      );

    const period =
      (
        brand?.periods || []
      ).find(
        item =>
          cleanText(
            item.periodId
          ) ===
          cleanText(
            periodId
          )
      );

    if (!period) {
      return [];
    }

    const publicBase =
      publicBaseProperties({
        school,
        profile
      });

    const hiddenBase =
      hiddenBaseProperties({
        school,
        profile,
        brand
      });

    const selected =
      safe.periods[
        cleanText(periodId)
      ] || {};

    const items = [];

    for (
      const [
        label,
        selection
      ]
      of Object.entries(
        selected
      )
    ) {
      if (
        selection?.submit ===
          false ||
        !selection?.variantId
      ) {
        continue;
      }

      items.push({
        id:
          Number(
            selection.variantId
          ),

        quantity: 1,

        properties: {
          ...publicBase,

          Period:
            cleanText(
              period.label
            ),

          Day:
            cleanText(label),

          Flavour:
            cleanText(
              selection.choice
            ),

          ...hiddenBase,

          _BYLPeriodId:
  cleanText(
    period.periodId
  ),

_BYLServiceDate:
  `${cleanText(
    period.periodId
  )}:${cleanText(
    label
  )}`,

...periodCutoffSnapshotProperties(
  period
),

_BYLItemType:
  "Main"
        }
      });
    }

    return items;
  }


  function buildCartItems({
    school,
    profile,
    brand,
    selections,
    periodId
  }) {
    if (
      !school ||
      !profile ||
      !brand
    ) {
      return [];
    }

    return (
      getOrderMode(
        brand
      ) ===
      ORDER_MODES.MONTHLY_WEEKDAYS
    )
      ? buildMonthlyCartItems({
          school,
          profile,
          brand,
          selections,
          periodId
        })
      : buildDateCartItems({
          school,
          profile,
          brand,
          selections
        });
  }


  /* =========================================================
     VARIANT REQUIREMENTS
     ========================================================= */

  function variantIdsForBrand(
    brand
  ) {
    const ids = [];

    for (
      const item
      of brand?.items || []
    ) {
      if (item.variantId) {
        ids.push(
          item.variantId
        );
      }
    }

    for (
      const period
      of brand?.periods || []
    ) {
      for (
        const option
        of period.options || []
      ) {
        if (
          option.variantId
        ) {
          ids.push(
            option.variantId
          );
        }
      }
    }

    return unique(ids);
  }


  async function preloadBrandVariants(
    brand
  ) {
    return services.variants.getMany(
      variantIdsForBrand(
        brand
      )
    );
  }


  /* =========================================================
     PERIOD HELPERS
     ========================================================= */

  function periodRows(
    brand,
    periodId
  ) {
    const period =
      (
        brand?.periods || []
      ).find(
        item =>
          cleanText(
            item.periodId
          ) ===
          cleanText(
            periodId
          )
      );

    if (!period) {
      return [];
    }

    return (
      period.options || []
    ).map(option => ({
      label:
        cleanText(
          option.label
        ),

      variantId:
        cleanText(
          option.variantId
        ),

      itemPrice:
        priceFromRecord(
          option
        )
    }));
  }


  function choicesForPeriodRow(
    brand,
    row
  ) {
    return choiceOptions(
      brand
    ).map(choice => ({
      value:
        cleanText(
          choice.value
        ),

      submit:
        choice.submit !== false,

      variantId:
        row.variantId,

      itemPrice:
        row.itemPrice,

      group:
        cleanText(
          choice.group
        )
    }));
  }


  /* =========================================================
     BRAND AVAILABILITY
     ========================================================= */

  function brandIsOpen(
    brand,
    now = new Date()
  ) {
    return (
      getOrderMode(
        brand
      ) ===
      ORDER_MODES.MONTHLY_WEEKDAYS
    )
      ? getOpenPeriods(
          brand,
          now
        ).length > 0
      : getOpenDates(
          brand,
          now
        ).length > 0;
  }


  /* =========================================================
     PUBLIC API
     ========================================================= */

  BYL.engine = {
    ORDER_MODES,

    helpers: {
      cleanText,
      normalize,
      formatDate,
      parseDateValue
    },

    brands: {
      getOrderMode,
      isOpen:
        brandIsOpen,

      mainItems,
      addOnItems,
      availableForProfile:
        brandAvailableForProfile,
      itemAvailableForProfile,
      toppingOptions,
      genericOptions,
      choiceOptions,

      variantIds:
        variantIdsForBrand,

      preloadVariants:
        preloadBrandVariants
    },

    cutoffs: {
      forDate:
        cutoffForServiceDate,

      dateIsOpen:
        isServiceDateOpen,

      openDates:
        getOpenDates,

      label:
        getCutoffLabel,

      periodDeadline,

      periodIsOpen:
        isPeriodOpen,

      openPeriods:
        getOpenPeriods
    },

    history: {
      isActive:
        isOrderLineActive,

      activeLines:
        activeOrderLines,

      serviceDate:
        lineServiceDate,

      lineBrandId,

      matchesSchool:
        lineMatchesSchool,

      matchesProfile:
        lineMatchesProfile,

      findExisting:
        findExistingOrder
    },

    selections: {
      create:
        createSelections,

      ensure:
        ensureSelections,

      setMain:
        setMainItem,

      toggleAddOn,

      toggleTopping,

      setOption:
        setGenericOption,

      setCustomInput,

      availableForDate:
        selectionAvailableForDate,

      invalidDates:
        invalidDateSelections,

      missingCustomInputs:
        missingRequiredCustomInputs,

      setPeriodChoice,

      lines:
        getSelectionLines
    },

    pricing: {
      total:
        calculateTotal
    },

    monthly: {
      rows:
        periodRows,

      choices:
        choicesForPeriodRow
    },

    cart: {
      publicBaseProperties,

      hiddenBaseProperties,

      build:
        buildCartItems
    }
  };

})();
